import { createPortal } from "react-dom";

/**
 * Review popup for inbound Google Sheet edits (sheet → system). Shows the
 * detected change set grouped into updates / additions / deletions and lets the
 * admin approve or reject the whole set. Rejecting keeps it around (approvable
 * later from "Pending changes"); approving snapshots the page first, server-side.
 */
const fmtPrice = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!isFinite(n)) return String(v);
  if (n === 0) return "Free";
  return Number.isInteger(n) ? n.toString() : n.toFixed(2);
};

function Money({ value, symbol }) {
  if (value === null || value === undefined) return <span className="diff-val empty">blank</span>;
  if (Number(value) === 0) return <span className="diff-val">Free</span>;
  return <span className="diff-val">{symbol}{fmtPrice(value)}</span>;
}

function Note({ value }) {
  const s = (value ?? "").toString().trim();
  if (!s) return <span className="diff-val empty">blank</span>;
  return <span className="diff-val">{s}</span>;
}

function UpdateRow({ op, symbol }) {
  const ch = op.changes || {};
  return (
    <div className="diff-row diff-update">
      <div className="diff-head">
        <span className="diff-mark mark-update">~</span>
        <span className="diff-name">{op.name}</span>
        <span className="diff-id">#{op.page_seq}</span>
      </div>
      <div className="diff-fields">
        {ch.name && (
          <div className="diff-field">
            <span className="diff-label">Name</span>
            <span className="diff-val">{ch.name.from}</span>
            <span className="diff-arrow">→</span>
            <span className="diff-val to">{ch.name.to}</span>
          </div>
        )}
        {ch.price && (
          <div className="diff-field">
            <span className="diff-label">Price</span>
            <Money value={ch.price.from} symbol={symbol} />
            <span className="diff-arrow">→</span>
            <span className="to"><Money value={ch.price.to} symbol={symbol} /></span>
            {ch.price.ignored && (
              <span className="diff-ignored" title="Price is derived from hours on this page, so this edit won't be applied">
                ignored (synced)
              </span>
            )}
          </div>
        )}
        {ch.agreed && (
          <div className="diff-field">
            <span className="diff-label">Agreed</span>
            <span className="diff-val">{ch.agreed.from ? "Yes" : "No"}</span>
            <span className="diff-arrow">→</span>
            <span className="diff-val to">{ch.agreed.to ? "Yes" : "No"}</span>
          </div>
        )}
        {ch.note && (
          <div className="diff-field">
            <span className="diff-label">Note</span>
            <Note value={ch.note.from} />
            <span className="diff-arrow">→</span>
            <span className="to"><Note value={ch.note.to} /></span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function InboundChangesModal({
  open,
  changeSet,
  currencySymbol = "$",
  busy = false,
  onApprove,
  onReject,
  onClose,
}) {
  if (!open || !changeSet) return null;

  const ops = changeSet.ops || [];
  const updates = ops.filter((o) => o.type === "update");
  const adds = ops.filter((o) => o.type === "add");
  const deletes = ops.filter((o) => o.type === "delete");
  const warnings = changeSet.warnings || [];

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card inbound-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{ maxWidth: 640 }}
      >
        <h3>Changes from the Google Sheet</h3>
        <p className="muted" style={{ marginTop: 8 }}>
          Someone edited the linked sheet. Review the changes below, then approve to
          pull them in (the current version is snapshotted first) or reject to keep
          things as they are. {changeSet.status === "rejected" && (
            <strong> You rejected these earlier; they're still here to approve.</strong>
          )}
        </p>

        <div className="inbound-summary">
          <span className="pill pill-update">{updates.length} edited</span>
          <span className="pill pill-add">{adds.length} added</span>
          <span className="pill pill-del">{deletes.length} removed</span>
        </div>

        <div className="inbound-body">
          {updates.length > 0 && (
            <section>
              <h4 className="diff-section">Edited</h4>
              {updates.map((op) => (
                <UpdateRow key={`u-${op.page_seq}`} op={op} symbol={currencySymbol} />
              ))}
            </section>
          )}

          {adds.length > 0 && (
            <section>
              <h4 className="diff-section">Added in the sheet</h4>
              {adds.map((op, i) => (
                <div className="diff-row diff-add" key={`a-${i}`}>
                  <div className="diff-head">
                    <span className="diff-mark mark-add">+</span>
                    <span className="diff-name">{op.name}</span>
                  </div>
                  <div className="diff-fields">
                    <div className="diff-field">
                      <span className="diff-label">Price</span>
                      <Money value={op.price} symbol={currencySymbol} />
                      {op.price_ignored && (
                        <span className="diff-ignored" title="Price is derived from hours on this page">
                          price ignored (synced)
                        </span>
                      )}
                      <span className="diff-label" style={{ marginLeft: 12 }}>Agreed</span>
                      <span className="diff-val">{op.agreed ? "Yes" : "No"}</span>
                    </div>
                    {(op.note ?? "").toString().trim() && (
                      <div className="diff-field">
                        <span className="diff-label">Note</span>
                        <Note value={op.note} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </section>
          )}

          {deletes.length > 0 && (
            <section>
              <h4 className="diff-section">Removed in the sheet</h4>
              {deletes.map((op) => (
                <div className="diff-row diff-del" key={`d-${op.page_seq}`}>
                  <div className="diff-head">
                    <span className="diff-mark mark-del">−</span>
                    <span className="diff-name strike">{op.name}</span>
                    <span className="diff-id">#{op.page_seq}</span>
                  </div>
                </div>
              ))}
            </section>
          )}

          {warnings.length > 0 && (
            <div className="banner warn" style={{ marginTop: 12, whiteSpace: "pre-wrap" }}>
              {warnings.map((w, i) => (
                <div key={i}>{w}</div>
              ))}
            </div>
          )}
        </div>

        <div className="row-h" style={{ justifyContent: "flex-end", marginTop: 20, gap: 8 }}>
          <button type="button" onClick={onClose} disabled={busy}>Decide later</button>
          <button type="button" className="danger-action" onClick={onReject} disabled={busy}>
            Reject
          </button>
          <button type="button" className="primary" onClick={onApprove} disabled={busy}>
            Approve {ops.length} change{ops.length === 1 ? "" : "s"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
